import type { SessionResponseDto } from '@/generated/api/models';

/**
 * The two-phase auth flow as a small, explicit state machine (a typed reducer —
 * XState would be overkill for one linear path with a single retry loop):
 *
 *   initializing → unauthenticated → signingIn → providerAuthed → loadingProfile → authenticated
 *                                                                      │
 *                                       (attempt < MAX: backoff retry) ┘
 *                                       (attempt ≥ MAX)  → signingOut → unauthenticated
 *
 * Phase 1 proves identity with the provider (`signingIn` → `providerAuthed`);
 * phase 2 exchanges it for the fleetster session profile (`loadingProfile`).
 */
export type AuthPhase =
  | 'initializing'
  | 'unauthenticated'
  | 'signingIn'
  | 'providerAuthed'
  | 'loadingProfile'
  | 'authenticated'
  | 'signingOut';

export interface MachineState {
  phase: AuthPhase;
  /** The loaded profile — set only in the `authenticated` phase. */
  session: SessionResponseDto | null;
  /** Profile-fetch retry counter, reset whenever a fresh fetch begins. */
  attempt: number;
  error: string | null;
}

export const initialMachineState: MachineState = {
  phase: 'initializing',
  session: null,
  attempt: 0,
  error: null,
};

export const MAX_PROFILE_ATTEMPTS = 3;

export type AuthEvent =
  | { type: 'BOOTSTRAP_FOUND'; session: SessionResponseDto }
  | { type: 'BOOTSTRAP_EMPTY' }
  | { type: 'SIGN_IN_START' }
  | { type: 'SIGN_IN_FAIL'; error: string }
  | { type: 'PROVIDER_AUTHED' }
  | { type: 'PROFILE_LOADED'; session: SessionResponseDto }
  | { type: 'PROFILE_RETRY' }
  | { type: 'SIGN_OUT_START' }
  | { type: 'SIGN_OUT_DONE' };

export function authReducer(state: MachineState, event: AuthEvent): MachineState {
  switch (event.type) {
    case 'BOOTSTRAP_FOUND':
      return { phase: 'authenticated', session: event.session, attempt: 0, error: null };
    case 'BOOTSTRAP_EMPTY':
      return { ...state, phase: 'unauthenticated', session: null };
    case 'SIGN_IN_START':
      return { ...state, phase: 'signingIn', error: null };
    case 'SIGN_IN_FAIL':
      return { ...state, phase: 'unauthenticated', error: event.error };
    case 'PROVIDER_AUTHED':
      // Begin phase 2 — a fresh profile fetch, so reset the retry counter.
      return { ...state, phase: 'loadingProfile', attempt: 0, error: null };
    case 'PROFILE_LOADED':
      return { phase: 'authenticated', session: event.session, attempt: 0, error: null };
    case 'PROFILE_RETRY':
      // Stay in loadingProfile and bump the counter so the fetch effect re-fires.
      return { ...state, phase: 'loadingProfile', attempt: state.attempt + 1 };
    case 'SIGN_OUT_START':
      return { ...state, phase: 'signingOut' };
    case 'SIGN_OUT_DONE':
      return { phase: 'unauthenticated', session: null, attempt: 0, error: null };
    default:
      return state;
  }
}
